import { useState } from 'react';
import { Radio24, RadioSelected24, Star32, TemplateRating32 } from '@packages/themes/icons';
import { useIntl } from 'react-intl';
import { Box } from '../Box';
import { Typography } from '../Typography';
import { Button } from '../Button/Button';
import { messages } from './KimRating.messages';

export type KimRatingProps = {
  pageType: 'storefront' | 'detailpage' | 'serp';
};

/**
 * KimRating component description displayed in storybook
 * */
export const KimRating = ({ pageType = 'storefront' }: KimRatingProps) => {
  const [ratingValue, setRatingValue] = useState<number | null>(null);
  const { formatMessage } = useIntl();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        textAlign: 'center',
      }}
    >
      {/* position relative is needed for the speech bubble to be displayed above the colored box */}
      <Box sx={{ position: 'relative' }}>
        <TemplateRating32 sx={{ width: '5rem', height: '5rem', mt: 1.5 }} />
      </Box>
      <Box
        sx={{
          bgcolor: 'grey.light',
          width: '100%',
          // make this margin-padding-switch to let the speech bubble overlap the colored container
          mt: -6,
          pt: 6,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          paddingInline: 6,
        }}
      >
        <Typography variant="body3">{formatMessage(messages.helpUs)}</Typography>
        <Typography variant="body1" sx={{ pt: 1 }}>
          {pageType === 'storefront' && formatMessage(messages.questionStorefront)}
          {pageType === 'detailpage' && formatMessage(messages.questionDetailpage)}
          {pageType === 'serp' && formatMessage(messages.questionSerp)}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            pt: 3.5,
            width: '19.5rem',
            justifyItems: 'center',
          }}
        >
          {[
            formatMessage(messages.ratingVeryUnsatisfied),
            formatMessage(messages.ratingUnsatisfied),
            formatMessage(messages.ratingNor),
            formatMessage(messages.ratingSatisfied),
            formatMessage(messages.ratingVerySatisfied),
          ].map((text, index) => (
            <Box
              key={text}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                paddingInline: '0.1rem',
                cursor: 'pointer',
              }}
              onClick={() => {
                setRatingValue(index);
              }}
            >
              <Star32
                sx={{
                  width: '1.7rem',
                  height: '1.7rem',
                  stroke: 'black',
                  color: ratingValue === null || ratingValue < index ? 'white' : 'kim.main',
                }}
              />
              <Typography variant="body3" sx={{ mt: 1, fontSize: '0.6rem' }}>
                {text}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ pt: 5, pb: 2.25, visibility: ratingValue === null ? 'hidden' : 'visible' }}>
          <Button
            variant="outlined"
            // the following calculation of points is not accurate yet - it is adjusted when the API-call is built - the alert is removed then also
            onClick={() =>
              // eslint-disable-next-line no-alert
              alert(`you voted ${ratingValue && ratingValue + 1} points to page ${pageType}`)
            }
            sx={{ minWidth: '12rem' }}
          >
            {formatMessage(messages.ratingOnwards)}
          </Button>
          <Box>
            <RadioSelected24 sx={{ width: '0.5rem', height: '0.5rem' }} />
            <Radio24 sx={{ width: '0.5rem', height: '0.5rem' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
